import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaBan,
  FaCheckCircle,
  FaEye,
  FaEyeSlash,
  FaLock,
  FaMobileAlt,
} from "react-icons/fa";
import { clearImpersonationStorage } from "../utils/impersonation";
import {
  hydrateAccountLinkContext,
  writeStoredSession,
} from "../utils/accountLinks";
import { resetLogRocketSession } from "../utils/logRocket";
import { canUseWebLogin, normalizeRole } from "../utils/rbac";

/**
 * Beautiful, responsive login page with glassmorphism, icons, and a split layout.
 * - Mobile-first stacked layout
 * - Background gradient + floating blobs
 * - Form card with subtle glass effect
 * - Show/Hide password
 * - Inline validation + disabled/loading button state
 * - Keyboard-friendly (press Enter to submit)
 */

const LoginPage = () => {
  const navigate = useNavigate();
  const [number, setNumber] = useState("");
  const [password, setPassword] = useState("");
  const [agreeToPolicy, setAgreeToPolicy] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPwd, setShowPwd] = useState(false);
  const [webBlocked, setWebBlocked] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  const policyUrl = useMemo(
    () => "https://billsarthi.com/privacy-policy.html",
    []
  );
  const planBuyUrl = useMemo(() => "https://billsarthi.com/product/professional-plan-web-mobile/", []);
  const heroBgUrl = useMemo(
    () =>
      "https://images.pexels.com/photos/67468/pexels-photo-67468.jpeg?auto=compress&cs=tinysrgb&w=2200",
    []
  );
  const devDefaultMasterMobile =
    process.env.NODE_ENV !== "production" ? "9988777409" : "";
  const devDefaultMasterPassword =
    process.env.NODE_ENV !== "production" ? "112211" : "";
  const masterMobile = useMemo(
    () =>
      String(process.env.REACT_APP_MASTER_MOBILE || devDefaultMasterMobile).replace(
        /\D/g,
        ""
      ),
    [devDefaultMasterMobile]
  );
  const masterPassword = useMemo(
    () => process.env.REACT_APP_MASTER_PASSWORD || devDefaultMasterPassword,
    [devDefaultMasterPassword]
  );

  const validate = () => {
    const e = {};
    if (!number || number.replace(/\D/g, "").length !== 10)
      e.number = "Enter 10-digit mobile number";
    if (!password) e.password = "Password is required";
    if (!agreeToPolicy) e.policy = "Please accept the privacy policy";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const postLoginRequest = (payload) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", "https://qr.billsarthi.in/public/api/login/restro", true);
      xhr.setRequestHeader("Content-Type", "application/json");
      xhr.timeout = 15000;

      xhr.onload = () => {
        let parsed = {};
        try {
          parsed = JSON.parse(xhr.responseText || "{}");
        } catch {
          parsed = {};
        }
        resolve({
          ok: xhr.status >= 200 && xhr.status < 300,
          data: parsed,
        });
      };

      xhr.onerror = () => reject(new Error("Network request failed"));
      xhr.ontimeout = () => reject(new Error("Request timeout"));
      xhr.send(JSON.stringify(payload));
    });

  const handleLogin = async (evt) => {
    evt?.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const enteredMobile = number.replace(/\D/g, "");
      const masterLoginEnabled = Boolean(masterMobile && masterPassword);
      const isMasterCredentialMatch =
        masterLoginEnabled &&
        enteredMobile === masterMobile &&
        password === masterPassword;

      if (isMasterCredentialMatch) {
        clearImpersonationStorage();
        const masterSession = {
          data: {
            id: "master-user",
            name: "Master Admin",
            mobile_number: enteredMobile,
            role: "master",
            isWebAllowed: 1,
            isweballowed: 1,
            can_impersonate: 1,
          },
        };
        writeStoredSession(masterSession, { preserveAccountLinkContext: false });
        toast("Master login successful!", "success");
        navigate("/login/master");
        return;
      }

      const response = await postLoginRequest({
        mobile_number: number,
        password,
      });
      const data = response?.data || {};

      if (response.ok) {
        const user = data?.data || {};
        if (!canUseWebLogin(user)) {
          clearImpersonationStorage();
          resetLogRocketSession();
          localStorage.removeItem("userData");
          const roleName = normalizeRole(user);
          toast(
            `Web login is allowed only for Owner and Cashier. Current role: ${roleName}.`,
            "error"
          );
          return;
        }
        const rawIsWebAllowed =
          user?.isWebAllowed ??
          user?.isweballowed ??
          data?.isWebAllowed ??
          data?.isweballowed;

        if (rawIsWebAllowed === 0 || rawIsWebAllowed === "0" || rawIsWebAllowed === false) {
          clearImpersonationStorage();
          resetLogRocketSession();
          localStorage.removeItem("userData");
          setWebBlocked(true);
          toast("Mobile-only plan detected. Web login is not enabled.", "error");
          return;
        }

        setWebBlocked(false);
        clearImpersonationStorage();
        writeStoredSession(data, { preserveAccountLinkContext: false });
        await hydrateAccountLinkContext({ force: true });
        toast("Login successful!", "success");
        navigate("/dashboard");
      } else {
        toast(data.message || "Check Mobile Number or Password", "error");
      }
    } catch (error) {
      const msg = String(error?.message || "");
      if (
        msg.includes("Failed to fetch") ||
        msg.includes("Network request failed") ||
        msg.includes("Request timeout")
      ) {
        toast(
          "Request blocked or network unreachable. Please disable blocking extensions/VPN and try again.",
          "error"
        );
      } else {
        toast("Network error: " + msg, "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBuyWebPlan = () => {
    if (typeof window === "undefined") return;
    const opened = window.open(planBuyUrl, "_blank", "noopener,noreferrer");
    if (!opened) window.location.href = planBuyUrl;
  };

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <div style={sx.page}>
      {/* Background visuals */}
      <div style={sx.bgWrap} aria-hidden>
        <div
          style={{
            ...sx.bgImage,
            backgroundImage: `url(${heroBgUrl})`,
          }}
        />
        <div style={sx.bgOverlay} />
      </div>

      <div style={isMobile ? sx.shellMobile : sx.shell}>
        {/* Left brand/marketing side */}
        {!isMobile && (
          <section style={sx.left}>
            <div style={sx.brandCard}>
              <img
                src="https://billsarthi.com/wp-content/uploads/2025/03/logo-white-1.png"
                alt="BillSarthi logo"
                style={sx.logoImg}
              />
              {/* <h1 style={sx.h1}>BillSarthi</h1> */}
              <p style={sx.lead}>
                Smart, fast & reliable billing for modern restaurants.
              </p>
              <ul style={sx.bullets}>
                <li style={sx.bulletItem}>
                  <FaCheckCircle style={sx.whiteIcon} aria-hidden />
                  <span>Bluetooth printing</span>
                </li>
                <li style={sx.bulletItem}>
                  <FaCheckCircle style={sx.whiteIcon} aria-hidden />
                  <span>KOT & table management</span>
                </li>
                <li style={sx.bulletItem}>
                  <FaCheckCircle style={sx.whiteIcon} aria-hidden />
                  <span>GST-ready invoices</span>
                </li>
              </ul>
            </div>

            <div style={sx.signupCard}>
              <h3 style={{ margin: 0 }}>New here?</h3>
              <p style={{ opacity: 0.9, margin: "6px 0 14px" }}>
                Create an account and get started in minutes.
              </p>
              <button
                type="button"
                onClick={() => navigate("/signup")}
                style={sx.signUpBtn}
              >
                Create your account
              </button>
            </div>
          </section>
        )}

        {/* Right form side */}
        <section style={isMobile ? sx.rightMobile : sx.right}>
          {isMobile && (
            <div style={sx.mobileBrand}>
              <img
                src="https://billsarthi.com/wp-content/uploads/2025/03/logo-white-1.png"
                alt="BillSarthi logo"
                style={sx.logoImg}
              />
              <div>
                {/* <h1 style={{ ...sx.h1, margin: "0 0 4px" }}>BillSarthi</h1> */}
                {/* <p style={{ ...sx.lead, margin: 0, fontSize: 14 }}>
                  Smart billing for restaurants
                </p> */}
              </div>
            </div>
          )}
          <form onSubmit={handleLogin} style={isMobile ? { ...sx.formCard, width: "100%", padding: 18 } : sx.formCard}>
            {webBlocked ? (
              <div style={sx.webBlockedCard}>
                <div style={sx.webBlockedIcon} aria-hidden>
                  <FaBan size={28} color="#ffffff" />
                </div>
                <h2 style={{ ...sx.formTitle, textAlign: "center" }}>
                  Web Access Not Included
                </h2>
                <p style={sx.webBlockedText}>
                  Your account is currently on a mobile-only plan. You are not eligible for web login yet.
                </p>
                <button
                  type="button"
                  style={sx.upgradeBtn}
                  onClick={handleBuyWebPlan}
                >
                  Do you want to buy a web plan?
                </button>
                <button
                  type="button"
                  style={sx.backToLoginBtn}
                  onClick={() => setWebBlocked(false)}
                >
                  Back to login
                </button>
              </div>
            ) : (
              <>
                <h2 style={sx.formTitle}>Welcome back</h2>
                <p style={sx.subText}>Sign in to continue to your dashboard</p>

                {/* Mobile Number */}
                <label style={sx.label} htmlFor="mobile">
                  Mobile Number
                </label>
                <div
                  style={{
                    ...sx.inputWrap,
                    ...(errors.number ? sx.inputError : {}),
                  }}
                >
                  <span style={sx.inputIcon} aria-hidden>
                    <FaMobileAlt />
                  </span>
                  <input
                    id="mobile"
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={10}
                    placeholder="Enter 10-digit mobile number"
                    value={number}
                    onChange={(e) => setNumber(e.target.value.replace(/\D/g, ""))}
                    style={sx.input}
                  />
                </div>
                {errors.number && <div style={sx.errorText}>{errors.number}</div>}

                {/* Password */}
                <label style={sx.label} htmlFor="password">
                  Password
                </label>
                <div
                  style={{
                    ...sx.inputWrap,
                    ...(errors.password ? sx.inputError : {}),
                  }}
                >
                  <span style={sx.inputIcon} aria-hidden>
                    <FaLock />
                  </span>
                  <input
                    id="password"
                    type={showPwd ? "text" : "password"}
                    placeholder="Your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={sx.input}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((s) => !s)}
                    style={sx.eyeBtn}
                    aria-label={showPwd ? "Hide password" : "Show password"}
                  >
                    {showPwd ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {errors.password && (
                  <div style={sx.errorText}>{errors.password}</div>
                )}

                {/* Actions row */}
                <div
                  style={
                    isMobile
                      ? { ...sx.rowBetween, flexDirection: "column", alignItems: "flex-start", gap: 8 }
                      : sx.rowBetween
                  }
                >
                  <label style={sx.checkbox}>
                    <input
                      type="checkbox"
                      checked={agreeToPolicy}
                      onChange={(e) => setAgreeToPolicy(e.target.checked)}
                      style={sx.checkboxInput}
                    />
                    <span>
                      &nbsp;I agree to the{" "}
                      <a
                        href={policyUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={sx.link}
                      >
                        privacy policy
                      </a>
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={() => navigate("/forgot-password")}
                    style={sx.inlineLinkBtn}
                  >
                    Forgot password?
                  </button>
                </div>
                {errors.policy && (
                  <div style={{ ...sx.errorText, marginTop: 6 }}>
                    {errors.policy}
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  style={{ ...sx.loginBtn, ...(loading ? sx.loginBtnLoading : {}) }}
                >
                  {loading ? (
                    <span style={sx.spinner} aria-hidden />
                  ) : (
                    <>
                      <span style={{ marginRight: 8 }}>→</span> Sign In
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => navigate("/signup")}
                  style={sx.createAccountBtn}
                >
                  Create your account
                </button>

                <p style={sx.miniNote}>Tip: Use the same login as your mobile app.</p>
              </>
            )}
          </form>
          <button
            type="button"
            onClick={() => navigate("/login-help")}
            style={isMobile ? sx.needHelpBtnMobile : sx.needHelpBtn}
          >
            Need help?
          </button>
          {isMobile && (
            <div style={sx.mobileSignup}>
              <span style={{ color: "#dbeafe" }}>New here?</span>
              <button
                type="button"
                onClick={() => navigate("/signup")}
                style={sx.signUpBtn}
              >
                Create your account
              </button>
            </div>
          )}
        </section>
      </div>

      {/* Tiny toast system */}
      <div id="toast-root" style={sx.toastRoot} />
    </div>
  );
};

/* ---------- Styles (vanilla CSS-in-JS) ---------- */
const sx = {
  page: {
    minHeight: "100vh",
    position: "relative",
    isolation: "isolate",
    display: "grid",
    placeItems: "center",
    background: "#031625",
    color: "#e9f3ff",
    overflow: "hidden",
    fontFamily: "Inter, system-ui, Segoe UI, Roboto, Arial, sans-serif",
  },
  bgWrap: {
    position: "fixed",
    inset: 0,
    pointerEvents: "none",
    overflow: "hidden",
    zIndex: 0,
  },
  bgImage: {
    position: "absolute",
    inset: 0,
    backgroundSize: "cover",
    backgroundPosition: "center",
    filter: "none",
    transform: "none",
  },
  bgOverlay: {
    position: "absolute",
    inset: 0,
    background: "rgba(0,0,0,.74)",
  },

  shell: {
    width: "min(1100px, 94vw)",
    margin: "clamp(16px,2vw,32px)",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 24,
    alignItems: "start",
    position: "relative",
    zIndex: 1,
  },
  shellMobile: {
    width: "100%",
    maxWidth: 520,
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    position: "relative",
    zIndex: 1,
  },

  left: {
    background:
      "linear-gradient(180deg, rgba(255,255,255,.06), rgba(255,255,255,.02))",
    border: "1px solid rgba(255,255,255,.14)",
    borderRadius: 20,
    padding: 32,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    gap: 20,
    boxShadow: "0 20px 60px rgba(0,0,0,.25)",
    backdropFilter: "blur(10px)",
  },
  brandCard: {
    display: "flex",
    flexDirection: "column",
    gap: 18,
    padding: 8,
  },
  logoImg: {
    width: 160,
    height: "auto",
    objectFit: "contain",
    filter: "drop-shadow(0 8px 24px rgba(0,0,0,.35))",

   
  },
  h1: { fontSize: 36, margin: "14px 0 6px", letterSpacing: 0.2 },
  lead: { opacity: 0.9, marginBottom: 16 },
  bullets: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "grid",
    gap: 8,
    color: "#e7f7ff",
  },
  bulletItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  whiteIcon: {
    color: "#ffffff",
    flexShrink: 0,
  },

  signupCard: {
    background: "rgba(255,255,255,.06)",
    border: "1px solid rgba(255,255,255,.14)",
    borderRadius: 16,
    padding: 24,
    marginTop: 8,
  },
  signUpBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 52,
    padding: "12px 18px",
    borderRadius: 12,
    border: "1px solid rgba(185,246,255,.32)",
    background: "linear-gradient(180deg, rgba(255,255,255,.94), rgba(230,244,255,.9))",
    color: "#0a5c98",
    fontWeight: 700,
    textDecoration: "none",
    cursor: "pointer",
    boxShadow: "0 10px 30px rgba(0,0,0,.16)",
  },

  right: {
    display: "flex",
    alignItems: "stretch",
    flexDirection: "column",
    gap: 12,
  },
  rightMobile: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  formCard: {
    width: "100%",
    background:
      "linear-gradient(180deg, rgba(255,255,255,.14), rgba(255,255,255,.06))",
    border: "1px solid rgba(255,255,255,.22)",
    borderRadius: 18,
    padding: 32,
    boxShadow: "0 18px 60px rgba(0,0,0,.3)",
    backdropFilter: "blur(10px)",
  },
  mobileBrand: {
    display: "flex",
    alignItems: "center",
    alignContent: "center",
    justifyContent: "center",
  },
  mobileSignup: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "12px",
  },
  formTitle: { margin: 0, fontSize: 26, fontWeight: 800 },
  subText: { margin: "6px 0 18px", opacity: 0.9 },
  label: { fontSize: 13, margin: "10px 0 6px", display: "block", opacity: 0.9 },
  inputWrap: {
    display: "grid",
    gridTemplateColumns: "32px 1fr 36px",
    alignItems: "center",
    gap: 6,
    border: "1px solid rgba(255,255,255,.35)",
    borderRadius: 12,
    background: "rgba(2,16,28,.35)",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,.1)",
  },
  inputIcon: {
    textAlign: "center",
    opacity: 0.95,
    color: "#ffffff",
    display: "grid",
    placeItems: "center",
  },
  input: {
    outline: "none",
    color: "#eaf6ff",
    background: "transparent",
    border: "none",
    padding: "12px 6px",
    fontSize: 15,
  },
  eyeBtn: {
    appearance: "none",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: 16,
    opacity: 0.95,
    color: "#ffffff",
    display: "grid",
    placeItems: "center",
  },
  inputError: { border: "1px solid rgba(255,95,95,.8)" },
  errorText: { color: "#ffd1d1", fontSize: 12, marginTop: 4 },

  rowBetween: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginTop: 12,
  },
  checkbox: { fontSize: 13, display: "flex", alignItems: "center" },
  checkboxInput: {
    accentColor: "#22d3ee",
    width: 18,
    height: 18,
    cursor: "pointer",
  },
  link: { color: "#b9f6ff", textDecoration: "none" },
  inlineLinkBtn: {
    appearance: "none",
    border: "none",
    background: "transparent",
    padding: 0,
    color: "#b9f6ff",
    fontSize: 13,
    textDecoration: "none",
    cursor: "pointer",
  },

  loginBtn: {
    marginTop: 16,
    width: "100%",
    minHeight: 56,
    padding: "14px 16px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,.4)",
    background: "linear-gradient(90deg,#00c1ff,#15e2b8)",
    color: "#042031",
    fontWeight: 800,
    letterSpacing: 0.3,
    cursor: "pointer",
    position: "relative",
    overflow: "hidden",
  },
  loginBtnLoading: {
    opacity: 0.8,
    cursor: "not-allowed",
    filter: "grayscale(20%)",
  },
  createAccountBtn: {
    marginTop: 10,
    width: "100%",
    minHeight: 56,
    padding: "14px 16px",
    borderRadius: 12,
    border: "1px solid rgba(185,246,255,.45)",
    background: "rgba(3,22,37,.5)",
    color: "#e9f3ff",
    fontWeight: 700,
    letterSpacing: 0.2,
    cursor: "pointer",
    backdropFilter: "blur(8px)",
  },
  needHelpBtn: {
    marginLeft: "auto",
    minHeight: 42,
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px dashed rgba(185,246,255,.42)",
    background: "rgba(255,255,255,.05)",
    color: "#b9f6ff",
    fontWeight: 600,
    letterSpacing: 0.2,
    cursor: "pointer",
  },
  needHelpBtnMobile: {
    alignSelf: "flex-end",
    minHeight: 42,
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px dashed rgba(185,246,255,.42)",
    background: "rgba(255,255,255,.05)",
    color: "#b9f6ff",
    fontWeight: 600,
    letterSpacing: 0.2,
    cursor: "pointer",
  },
  spinner: {
    width: 18,
    height: 18,
    border: "2px solid rgba(255,255,255,.6)",
    borderTopColor: "transparent",
    borderRadius: "50%",
    display: "inline-block",
    animation: "spin .9s linear infinite",
  },
  miniNote: { marginTop: 10, fontSize: 12, opacity: 0.8 },
  webBlockedCard: {
    minHeight: 320,
    display: "grid",
    justifyItems: "center",
    alignContent: "center",
    gap: 12,
    textAlign: "center",
    padding: "14px 4px",
  },
  webBlockedIcon: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    fontSize: 28,
    background: "rgba(255, 109, 109, 0.2)",
    border: "1px solid rgba(255, 145, 145, 0.55)",
  },
  webBlockedText: {
    margin: 0,
    fontSize: 14,
    lineHeight: 1.6,
    maxWidth: 360,
    color: "#eaf6ff",
    opacity: 0.95,
  },
  upgradeBtn: {
    marginTop: 8,
    padding: "12px 16px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,.4)",
    background: "linear-gradient(90deg,#ffd37a,#ffad66)",
    color: "#3c2300",
    fontWeight: 800,
    cursor: "pointer",
  },
  backToLoginBtn: {
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,.35)",
    background: "rgba(0,0,0,.15)",
    color: "#dff1ff",
    fontWeight: 700,
    cursor: "pointer",
  },

  toastRoot: {
    position: "fixed",
    right: 16,
    bottom: 16,
    display: "grid",
    gap: 8,
    zIndex: 50,
  },
};

/* Lightweight toast */
function toast(msg, type = "info") {
  const root = document.getElementById("toast-root");
  if (!root) return alert(msg);
  const el = document.createElement("div");
  el.style.padding = "10px 14px";
  el.style.borderRadius = "10px";
  el.style.backdropFilter = "blur(8px)";
  el.style.border = "1px solid rgba(255,255,255,.25)";
  el.style.color = "#001726";
  el.style.fontWeight = "700";
  el.style.boxShadow = "0 10px 28px rgba(0,0,0,.35)";
  el.style.background =
    type === "success"
      ? "linear-gradient(90deg,#a8ff78,#78ffd6)"
      : type === "error"
      ? "linear-gradient(90deg,#ffcccb,#ffd1d1)"
      : "linear-gradient(90deg,#e0f3ff,#bff3ff)";
  el.textContent = msg;
  root.appendChild(el);
  setTimeout(() => {
    el.style.transition = "all .4s ease";
    el.style.opacity = "0";
    el.style.transform = "translateY(10px)";
    setTimeout(() => el.remove(), 420);
  }, 2200);
}

/* Keyframe injection for spinner (scoped) */
const styleTag = document.createElement("style");
styleTag.innerHTML = `@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}
@media (max-width: 920px){
  /* Stack on mobile */
  .stackShell{grid-template-columns:1fr!important}
}`;
document.head.appendChild(styleTag);

// Responsive helper class injection
sx.shell.className = "stackShell"; // not used directly, but keeps media query semantics clear

export default LoginPage;

// Mobile-specific styles injected once
if (typeof document !== "undefined" && !document.getElementById("login-mobile-css")) {
  const style = document.createElement("style");
  style.id = "login-mobile-css";
  style.textContent = `
    @media (max-width: 768px) {
      body { background: #031625; }
    }
  `;
  document.head.appendChild(style);
}
